import type { NotificationMessageResponse } from "@9amhealth/openapi";
import { NotificationMessageControllerService } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { globalEvents } from "src/constants/globalEvents";
import { onAppActive } from "src/hybrid/appLifecycle";
import reportErrorSentry from "src/lib/reportErrorSentry";
import WebSocketBloc, {
  WebsocketMessageType
} from "src/state/WebSocketBloc/WebSocketBloc";
import type { FileRef } from "@9amhealth/shared";
import { extractFileRefsFromMetadata } from "@9amhealth/shared";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

const READ_PAGE_SIZE = 20;
const UNREAD_PAGE_SIZE = 50;
const LATEST_SORT = ["addedAt,desc"];
export interface NotificationDisplayItem {
  id: string;
  isUnread: boolean;
  isHtml: boolean;
  rawContent: string;
  previewContent: string;
  addedAt: string;
  files: FileRef[];
}

export type DateGroupLabelKey = "today" | "yesterday" | "date";

export interface DateGroup {
  labelKey: DateGroupLabelKey;
  dateValue: string;
  items: NotificationDisplayItem[];
}

export interface NotificationsState {
  notifications: NotificationMessageResponse[];
  unreadCount: number;
  readNotifications: NotificationMessageResponse[];
  readPage: number;
  hasMoreRead: boolean;
  loading: boolean;
  loadingRead: boolean;
  initialLoadComplete: boolean;
}

const INITIAL_STATE: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  readNotifications: [],
  readPage: 0,
  hasMoreRead: true,
  loading: false,
  loadingRead: false,
  initialLoadComplete: false
};

// --- Pure helpers ---

const previewHtml = (html: string): string =>
  html
    .replace(/<\/(?:p|div|li|br|h[1-6])>/gi, " \u2022 ")
    .replace(/<(?:p|div|li|br|h[1-6])[^>]*>/gi, "")
    .replace(/<(?!\/?\b(?:b|strong)\b)[^>]+>/gi, "")
    .replace(/\s+/g, " ")
    .replace(/ ?\u2022 ?/g, " \u2022 ")
    .replace(/^[\s\u2022]+|[\s\u2022]+$/g, "")
    .trim();

const toDisplayItem = (
  n: NotificationMessageResponse
): NotificationDisplayItem => {
  const isHtml = n.message.contentType === "text/html";
  const rawContent = n.message.contentValue ?? "";
  return {
    id: n.id,
    isUnread: !n.readAt,
    isHtml,
    rawContent,
    previewContent: isHtml ? previewHtml(rawContent) : rawContent,
    addedAt: n.addedAt,
    files: extractFileRefsFromMetadata(n.message.metadata)
  };
};

const sortNewestFirst = (
  a: NotificationDisplayItem,
  b: NotificationDisplayItem
) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();

const getDateGroupLabelKey = (dateStr: string): DateGroupLabelKey => {
  const date = new Date(dateStr);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return "today";
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return "yesterday";
  }
  return "date";
};

const groupByDate = (items: NotificationDisplayItem[]): DateGroup[] => {
  const sorted = [...items].sort(sortNewestFirst);
  const groups = new Map<string, NotificationDisplayItem[]>();

  for (const item of sorted) {
    const key = new Date(item.addedAt).toDateString();
    const existing = groups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return Array.from(groups.entries()).map(([, groupItems]) => ({
    labelKey: getDateGroupLabelKey(groupItems[0].addedAt),
    dateValue: groupItems[0].addedAt,
    items: groupItems
  }));
};

// --- Cubit ---

export default class NotificationsCubit extends Cubit<NotificationsState> {
  static keepAlive = true;

  private cachedAllItems: NotificationDisplayItem[] = [];
  private cachedDateGroups: DateGroup[] = [];
  private cacheKey = "";

  constructor() {
    super({ ...INITIAL_STATE });

    onUserSessionReset(this.handleUserClear);
    this.setupWebSocketListeners();
    this.setupAppLifecycleListeners();
  }

  // -- Lifecycle --

  private readonly setupWebSocketListeners = (): void => {
    Blac.getBloc(WebSocketBloc).addObserver(
      WebsocketMessageType.notificationCreated,
      () => {
        void this.loadUnread();
      }
    );
  };

  private readonly setupAppLifecycleListeners = (): void => {
    onAppActive(() => {
      if (this.state.initialLoadComplete) {
        void this.loadUnread();
      }
    });

    document.addEventListener(
      globalEvents.PUSH_NOTIFICATION_ACTION,
      this.handlePushNotificationAction
    );
  };

  private readonly handlePushNotificationAction = (): void => {
    void this.loadUnread();
  };

  private readonly handleUserClear = (): void => {
    this.cacheKey = "";
    this.patch({ ...INITIAL_STATE });
  };

  // -- Computed (cached) --

  private buildCacheKey(): string {
    const { notifications, readNotifications } = this.state;
    return `${notifications.length}:${notifications[0]?.id ?? ""}:${readNotifications.length}:${readNotifications[0]?.id ?? ""}`;
  }

  private ensureCache(): void {
    const key = this.buildCacheKey();
    if (key === this.cacheKey) {
      return;
    }
    this.cacheKey = key;

    this.cachedAllItems = [
      ...this.state.notifications.map(toDisplayItem),
      ...this.state.readNotifications.map(toDisplayItem)
    ].sort(sortNewestFirst);

    this.cachedDateGroups = groupByDate(this.cachedAllItems);
  }

  get allDisplayItems(): NotificationDisplayItem[] {
    this.ensureCache();
    return this.cachedAllItems;
  }

  get dateGroups(): DateGroup[] {
    this.ensureCache();
    return this.cachedDateGroups;
  }

  get isEmpty(): boolean {
    return (
      !this.state.loading &&
      this.state.notifications.length === 0 &&
      this.state.readNotifications.length === 0
    );
  }

  // -- Data loading --

  readonly loadUnread = async (): Promise<void> => {
    this.patch({ loading: true });

    try {
      const result = await NotificationMessageControllerService.getUnread(
        0,
        UNREAD_PAGE_SIZE,
        LATEST_SORT
      );
      const notifications = result.data.content;
      this.patch({
        notifications,
        unreadCount: notifications.length,
        loading: false,
        initialLoadComplete: true
      });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ loading: false, initialLoadComplete: true });
    }
  };

  readonly loadReadNotifications = async (): Promise<void> => {
    if (this.state.loadingRead) {
      return;
    }

    this.patch({ loadingRead: true });

    try {
      const result = await NotificationMessageControllerService.getRead(
        0,
        READ_PAGE_SIZE,
        LATEST_SORT
      );
      const items = result.data.content;
      const totalPages = result.data.page.totalPages;
      this.patch({
        readNotifications: items,
        readPage: 0,
        hasMoreRead: totalPages > 1,
        loadingRead: false
      });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ loadingRead: false });
    }
  };

  readonly loadMoreRead = async (): Promise<void> => {
    if (this.state.loadingRead || !this.state.hasMoreRead) {
      return;
    }

    const nextPage = this.state.readPage + 1;
    this.patch({ loadingRead: true });

    try {
      const result = await NotificationMessageControllerService.getRead(
        nextPage,
        READ_PAGE_SIZE,
        LATEST_SORT
      );
      const items = result.data.content;
      const totalPages = result.data.page.totalPages;
      this.patch({
        readNotifications: [...this.state.readNotifications, ...items],
        readPage: nextPage,
        hasMoreRead: nextPage + 1 < totalPages,
        loadingRead: false
      });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ loadingRead: false });
    }
  };

  // -- Actions --

  readonly markAsRead = async (id: string): Promise<void> => {
    const notification = this.state.notifications.find((n) => n.id === id);
    if (!notification || notification.readAt) {
      return;
    }

    const now = new Date().toISOString();
    const updated = { ...notification, readAt: now };
    this.patch({
      notifications: this.state.notifications.filter((n) => n.id !== id),
      unreadCount: Math.max(0, this.state.unreadCount - 1),
      readNotifications: [updated, ...this.state.readNotifications]
    });

    try {
      await NotificationMessageControllerService.update(id, { read: true });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  readonly markAllAsRead = async (): Promise<void> => {
    const unread = [...this.state.notifications];
    if (unread.length === 0) {
      return;
    }

    const now = new Date().toISOString();
    const updatedItems = unread.map((n) => ({ ...n, readAt: now }));

    this.patch({
      notifications: [],
      unreadCount: 0,
      readNotifications: [...updatedItems, ...this.state.readNotifications]
    });

    await Promise.allSettled(
      unread.map((n) =>
        NotificationMessageControllerService.update(n.id, {
          read: true
        }).catch(reportErrorSentry)
      )
    );
  };

  readonly startConversation = async (
    notificationId: string
  ): Promise<string | null> => {
    try {
      const result =
        await NotificationMessageControllerService.createConversationFromNotification(
          notificationId
        );
      return result.data.conversationId;
    } catch (e) {
      reportErrorSentry(e);
      return null;
    }
  };
}
